import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../../../../../firebase/AuthContext";
import { getApiUrl } from "../../../../../config/api";
import CompetitorProductCard, {
    ComplianceBadge,
} from "./CompetitorProductCard";
import CompetitorProductModal from "./CompetitorProductModal";

// ── Simple Detail Modal ────────────────────────────────────────────────────────

// ── Main View ──────────────────────────────────────────────────────────────────
function ProductCompetitorView({
    language,
    loading,
    products,
    pagination = { page: 1, per_page: 20, total: 0, pages: 1 },
    categories = [],
    competitors = [],
    categoryFilter,
    competitorFilter,
    searchQuery = "",
    onCategoryChange,
    onCompetitorChange,
    loadingSearch,
    onSearchChange,
    onPageChange,
    onProductDeleted,
    onNavigateToUpload,
}) {
    const { currentUser, hasRole } = useAuth();
    const canCreate = hasRole && hasRole("competitor.create");
    const canDelete = hasRole && hasRole("competitor.delete");

    const [viewMode, setViewMode] = useState("grid");
    const [searchInput, setSearchInput] = useState(searchQuery || "");

    // Modals
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [deletingProduct, setDeletingProduct] = useState(false);

    const commitSearch = () => onSearchChange?.(searchInput);
    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") commitSearch();
    };

    const handleDelete = async () => {
        if (!productToDelete) return;
        setDeletingProduct(true);
        try {
            const token = await currentUser.getIdToken();
            await axios.delete(
                `${getApiUrl()}/api/v2/competitor-products/${productToDelete.product_id}`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setShowDeleteModal(false);
            setProductToDelete(null);
            onProductDeleted?.();
        } catch (err) {
            console.error("Delete failed:", err);
        } finally {
            setDeletingProduct(false);
        }
    };

    // ── Pagination bar (matches DatabaseView) ─────────────────────────────────
    const PaginationBar = () => {
        if (pagination.pages <= 1) return null;
        return (
            <div className="flex items-center justify-between bg-white rounded-xl px-6 py-4 border border-gray-200 shadow-sm">
                <p className="text-sm text-gray-500">
                    {language === "zh"
                        ? `第 ${pagination.page} 頁，共 ${pagination.pages} 頁 (${pagination.total} 個產品)`
                        : `Page ${pagination.page} of ${pagination.pages} (${pagination.total} products)`}
                </p>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onPageChange?.(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                        className="p-2 rounded-lg text-gray-500 hover:bg-orange-50 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                    </button>
                    {Array.from(
                        { length: Math.min(pagination.pages, 7) },
                        (_, i) => {
                            let p;
                            if (pagination.pages <= 7) p = i + 1;
                            else if (pagination.page <= 4) p = i + 1;
                            else if (pagination.page >= pagination.pages - 3)
                                p = pagination.pages - 6 + i;
                            else p = pagination.page - 3 + i;
                            return (
                                <button
                                    key={p}
                                    onClick={() => onPageChange?.(p)}
                                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                                        p === pagination.page
                                            ? "bg-orange-500 text-white shadow-sm"
                                            : "text-gray-600 hover:bg-orange-50 hover:text-orange-500"
                                    }`}
                                >
                                    {p}
                                </button>
                            );
                        },
                    )}
                    <button
                        onClick={() => onPageChange?.(pagination.page + 1)}
                        disabled={pagination.page >= pagination.pages}
                        className="p-2 rounded-lg text-gray-500 hover:bg-orange-50 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="relative">
            {/* ── Header card (matches DatabaseView) ───────────────────────── */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                    {/* Title + count */}
                    <div className="flex-1 min-w-0">
                        <h2 className="text-xl font-bold text-gray-900 mb-2">
                            {language === "zh"
                                ? "競品數據庫"
                                : "Competitor Database"}
                        </h2>
                        <p className="text-gray-600 text-sm">
                            {pagination.total}{" "}
                            {language === "zh" ? "個產品" : "products"}
                            {(searchQuery ||
                                categoryFilter ||
                                competitorFilter) &&
                                ` ${language === "zh" ? "符合搜索條件" : "found"}`}
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
                        {/* Search */}
                        <div className="flex flex-col items-start gap-2">
                            <div className="flex items-center gap-2 flex-1 w-full">
                                <div className="relative flex-1 lg:w-56">
                                    <svg
                                        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                        />
                                    </svg>
                                    <input
                                        type="text"
                                        value={searchInput}
                                        onChange={(e) =>
                                            setSearchInput(e.target.value)
                                        }
                                        onKeyDown={handleSearchKeyDown}
                                        placeholder={
                                            language === "zh"
                                                ? "搜索型號或描述..."
                                                : "Search model ID or description..."
                                        }
                                        className="w-full pl-10 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-200 transition-all text-sm"
                                    />
                                    {searchInput && (
                                        <button
                                            onClick={() => {
                                                setSearchInput("");
                                                onSearchChange?.("");
                                            }}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                            title={
                                                language === "zh"
                                                    ? "清除"
                                                    : "Clear"
                                            }
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M6 18L18 6M6 6l12 12"
                                                />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                <button
                                    onClick={commitSearch}
                                    className="px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-xl transition-all shadow-sm flex-shrink-0"
                                >
                                    {language === "zh" ? "搜索" : "Search"}
                                </button>
                            </div>

                            {/* Category filter */}
                            <div className="flex flex-row gap-2 items-center w-auto">
                                <div className="relative lg:w-44">
                                    <select
                                        value={categoryFilter ?? ""}
                                        onChange={(e) =>
                                            onCategoryChange?.(
                                                e.target.value
                                                    ? Number(e.target.value)
                                                    : null,
                                            )
                                        }
                                        className="w-full pl-3 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-200 transition-all appearance-none"
                                    >
                                        <option value="">
                                            {language === "zh"
                                                ? "所有類別"
                                                : "All categories"}
                                        </option>
                                        {categories.map((c) => (
                                            <option
                                                key={c.category_id}
                                                value={c.category_id}
                                            >
                                                {c.sub_category
                                                    ? `${c.category_name} – ${c.sub_category}`
                                                    : c.category_name}
                                            </option>
                                        ))}
                                    </select>
                                    <svg
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 9l-7 7-7-7"
                                        />
                                    </svg>
                                </div>

                                {/* Competitor filter */}
                                <div className="relative lg:w-44">
                                    <select
                                        value={competitorFilter ?? ""}
                                        onChange={(e) =>
                                            onCompetitorChange?.(
                                                e.target.value
                                                    ? Number(e.target.value)
                                                    : null,
                                            )
                                        }
                                        className="w-full pl-3 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-200 transition-all appearance-none"
                                    >
                                        <option value="">
                                            {language === "zh"
                                                ? "所有競爭對手"
                                                : "All competitors"}
                                        </option>
                                        {competitors.map((c) => (
                                            <option
                                                key={c.competitor_id}
                                                value={c.competitor_id}
                                            >
                                                {c.competitor_string}
                                            </option>
                                        ))}
                                    </select>
                                    <svg
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 9l-7 7-7-7"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* View toggle */}
                        <div className="flex items-center bg-gray-300 rounded-xl p-1 border border-gray-300">
                            <button
                                onClick={() => setViewMode("grid")}
                                className={`p-2 rounded-xl transition-all ${viewMode === "grid" ? "bg-orange-500 text-white" : "text-gray-400 hover:text-orange-500"}`}
                            >
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                                    />
                                </svg>
                            </button>
                            <button
                                onClick={() => setViewMode("list")}
                                className={`p-2 rounded-xl transition-all ${viewMode === "list" ? "bg-orange-500 text-white" : "text-gray-400 hover:text-orange-500"}`}
                            >
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 6h16M4 10h16M4 14h16M4 18h16"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Loading state */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-24 gap-4">
                    <div className="flex gap-1">
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                className="w-3 h-3 bg-orange-500 rounded-full animate-bounce"
                                style={{ animationDelay: `${i * 0.1}s` }}
                            />
                        ))}
                    </div>
                    <p className="text-sm text-orange-600 font-medium">
                        {language === "zh" ? "正在加載..." : "Loading..."}
                    </p>
                </div>
            ) : loadingSearch ? (
                <div className="py-6">
                    {viewMode === "grid" ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="bg-white border border-gray-200 rounded-xl p-5 animate-pulse"
                                >
                                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
                                    <div className="h-3 bg-gray-100 rounded w-1/2 mb-4" />
                                    <div className="h-3 bg-gray-100 rounded w-full mb-2" />
                                    <div className="h-3 bg-gray-100 rounded w-5/6" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="bg-white border border-gray-200 rounded-xl p-5 animate-pulse"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="flex-1 space-y-2">
                                            <div className="h-4 bg-gray-200 rounded w-1/3" />
                                            <div className="h-3 bg-gray-100 rounded w-1/4" />
                                        </div>
                                        <div className="h-8 w-20 bg-gray-200 rounded-xl" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : products.length === 0 ? (
                /* Empty state */
                <div className="flex flex-col items-center justify-center py-24 text-center">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mb-4">
                        <svg
                            className="w-8 h-8 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-2">
                        {language === "zh" ? "暫無數據" : "No products found"}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                        {language === "zh"
                            ? "嘗試調整您的搜索或篩選條件"
                            : "Try adjusting your search or filters"}
                    </p>
                    {canCreate && onNavigateToUpload && (
                        <button
                            onClick={onNavigateToUpload}
                            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-xl transition-colors"
                        >
                            {language === "zh" ? "上傳產品" : "Upload Products"}
                        </button>
                    )}
                </div>
            ) : (
                <div className="py-6 flex flex-col gap-3">
                    <PaginationBar />
                    {/* Grid view */}
                    {viewMode === "grid" ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {products.map((product, index) => (
                                <CompetitorProductCard
                                    key={product.product_id}
                                    product={product}
                                    index={index}
                                    language={language}
                                    canManage={canDelete}
                                    onView={setSelectedProduct}
                                    onEdit={() => {}} // TODO: edit modal
                                    onDelete={(p) => {
                                        setProductToDelete(p);
                                        setShowDeleteModal(true);
                                    }}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {products.map((product, index) => (
                                <div
                                    key={product.product_id || product.id}
                                    className="group bg-white border border-gray-200 rounded-xl p-5 hover:border-orange-500/50 transition-all duration-300 hover:shadow-lg"
                                    style={{
                                        animationDelay: `${index * 30}ms`,
                                    }}
                                >
                                    <div className="flex items-center justify-between gap-6">
                                        {/* Left Section - Product Info */}
                                        <div className="flex items-start gap-4 flex-1 min-w-0">
                                            {/* <div className="w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                                                            <svg
                                                                className="w-6 h-6 text-orange-500"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={2}
                                                                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                                                />
                                                            </svg>
                                                        </div> */}
                                            <div className="min-w-0 flex-1 space-y-2">
                                                <div>
                                                    <h3 className="text-gray-900 font-bold text-lg truncate group-hover:text-orange-500 transition-colors uppercase">
                                                        {product.model_id ||
                                                            product.name}
                                                    </h3>
                                                    <p className="text-gray-500 text-sm capitalize">
                                                        {product.category}
                                                    </p>
                                                </div>

                                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                                                    <div className="flex items-center gap-1.5">
                                                        <svg
                                                            className="w-3.5 h-3.5 text-gray-400"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                                            />
                                                        </svg>
                                                        <span className="text-gray-700 font-medium capitalize">
                                                            {
                                                                product.competitor_string
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <svg
                                                            className="w-3.5 h-3.5 text-gray-400"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                                                            />
                                                        </svg>
                                                        <span className="text-gray-600 font-mono">
                                                            #
                                                            {product.product_id}
                                                        </span>
                                                    </div>
                                                    {product.pdf_url && (
                                                        <a
                                                            href={
                                                                product.pdf_url
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center gap-1.5 text-orange-500 hover:text-orange-600 hover:underline"
                                                        >
                                                            <svg
                                                                className="w-3.5 h-3.5"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                                                />
                                                            </svg>
                                                            {language === "zh"
                                                                ? "PDF"
                                                                : "PDF"}
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Middle Section - Compliance Badges */}
                                        <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
                                            {product.rohs_compliant && (
                                                <ComplianceBadge
                                                    label="RoHS"
                                                    isCompliant={
                                                        product.rohs_compliant
                                                    }
                                                />
                                            )}
                                            {product.reach_compliant && (
                                                <ComplianceBadge
                                                    label="REACH"
                                                    isCompliant={
                                                        product.reach_compliant
                                                    }
                                                />
                                            )}
                                            {product.ul_comparable && (
                                                <ComplianceBadge
                                                    label="UL"
                                                    isCompliant={
                                                        product.ul_comparable
                                                    }
                                                />
                                            )}
                                        </div>

                                        {/* Right Section - Action Buttons */}
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() =>
                                                    setSelectedProduct(product)
                                                }
                                                className="px-3 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all text-sm font-medium shadow-lg shadow-orange-500/30 flex items-center gap-1.5"
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                    />
                                                </svg>
                                                <span className="hidden xl:inline">
                                                    {language === "zh"
                                                        ? "詳情"
                                                        : "Details"}
                                                </span>
                                            </button>
                                            {/* <button
                                                            onClick={() =>
                                                                handleEditProduct(product)
                                                            }
                                                            className="p-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/30"
                                                            title={
                                                                language === "zh"
                                                                    ? "編輯產品"
                                                                    : "Edit Product"
                                                            }
                                                        >
                                                            <svg
                                                                className="w-4 h-4"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={2}
                                                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                                />
                                                            </svg>
                                                        </button> */}
                                            <button
                                                onClick={() => {
                                                    setProductToDelete(product);
                                                    setShowDeleteModal(true);
                                                }}
                                                className="p-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all shadow-lg shadow-red-500/30"
                                                title={
                                                    language === "zh"
                                                        ? "刪除產品"
                                                        : "Delete Product"
                                                }
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Mobile Compliance Badges */}
                                    <div className="flex lg:hidden items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                                        {product.rohs_compliant && (
                                            <ComplianceBadge
                                                label="RoHS"
                                                isCompliant={
                                                    product.rohs_compliant
                                                }
                                            />
                                        )}
                                        {product.reach_compliant && (
                                            <ComplianceBadge
                                                label="REACH"
                                                isCompliant={
                                                    product.reach_compliant
                                                }
                                            />
                                        )}
                                        {product.ul_comparable && (
                                            <ComplianceBadge
                                                label="UL"
                                                isCompliant={
                                                    product.ul_comparable
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <PaginationBar />
                </div>
            )}

            {/* Detail modal */}
            {selectedProduct && (
                <CompetitorProductModal
                    product={selectedProduct}
                    language={language}
                    onClose={() => setSelectedProduct(null)}
                />
            )}

            {/* Delete confirm modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowDeleteModal(false)}
                    />
                    <div
                        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="p-6 pb-4">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                                    <svg
                                        className="w-5 h-5 text-red-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        {language === "zh"
                                            ? "刪除產品"
                                            : "Delete Product"}
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-0.5">
                                        {language === "zh"
                                            ? "此操作無法撤銷"
                                            : "This action cannot be undone"}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="px-6 pb-4">
                            <p className="text-sm text-gray-600 mb-3">
                                {language === "zh"
                                    ? "您即將刪除："
                                    : "You are about to delete:"}
                            </p>
                            <p className="font-semibold text-gray-900 mb-4">
                                {productToDelete?.model_id}
                            </p>
                            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                                <svg
                                    className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                                    />
                                </svg>
                                <p className="text-xs text-red-700">
                                    {language === "zh"
                                        ? "刪除後，所有相關資料將永久移除，無法復原。"
                                        : "Once deleted, all associated data will be permanently removed and cannot be recovered."}
                                </p>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100 flex items-center justify-end gap-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                            >
                                {language === "zh" ? "取消" : "Cancel"}
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={deletingProduct}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-xl disabled:opacity-50 transition-colors flex items-center gap-2"
                            >
                                {deletingProduct && (
                                    <svg
                                        className="w-4 h-4 animate-spin"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        />
                                    </svg>
                                )}
                                {deletingProduct
                                    ? language === "zh"
                                        ? "刪除中..."
                                        : "Deleting..."
                                    : language === "zh"
                                      ? "刪除"
                                      : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating Add Product button — competitor.create only */}
            {canCreate && (
                <button
                    onClick={onNavigateToUpload}
                    className="fixed bottom-8 right-8 flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl shadow-lg hover:shadow-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-200 z-40 group"
                >
                    <svg
                        className="w-5 h-5 transition-transform group-hover:rotate-90 duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                        />
                    </svg>
                    <span className="text-sm font-semibold">
                        {language === "zh" ? "新增產品" : "Add Product"}
                    </span>
                </button>
            )}
        </div>
    );
}

export default ProductCompetitorView;
